import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { GenericForm, FormField } from '../../../components/GenericForm';
import { toast } from 'react-toastify';
import { SupplierModel, PaymentMethodEnum } from '../models/SupplierModel';
import { retrieveSupplier, updateSupplier } from '../services/SupplierCRUD';
import authorizedContasolEditors from '../../distributors/constants/authorizedContasolEditors';
import { useSelector } from 'react-redux';
import { authUserSelector } from '../../auth';
import { getChangedFields } from '../../../utils/getChangedFields';

const LOADING_STATUS = 'LOADING_STATUS';
const IDLE_STATUS = 'IDLE_STATUS';

export default function SupplierDetail({ id }) {
  const intl = useIntl();
  const [status, setStatus] = useState(IDLE_STATUS);
  const [error, setError]: any | undefined = useState({});
  const [editing, setEditing] = useState(false);
  const [supplier, setSupplier] = useState<SupplierModel | null>(null);
  const authUser = useSelector(authUserSelector);
  const formFields: Array<FormField<SupplierModel>> = [
    {
      id: 'name',
      label: intl.formatMessage({ id: 'suppliers.name' }),
      inputType: 'text',
    },
    {
      id: 'vat_number',
      label: intl.formatMessage({ id: 'suppliers.vatNumber' }),
      inputType: 'text',
    },
    {
      id: 'email',
      label: intl.formatMessage({ id: 'suppliers.email' }),
      inputType: 'email',
    },
    {
      id: 'phone',
      label: intl.formatMessage({ id: 'suppliers.phone' }),
      inputType: 'text',
      maxLength: 14,
    },
    {
      id: 'payment_method',
      label: intl.formatMessage({ id: 'suppliers.paymentMethod' }),
      inputType: 'select',
      options: [
        {
          value: PaymentMethodEnum.WIRE_TRANSFER,
          label: intl.formatMessage({ id: 'suppliers.wireTransfer' }),
        },
        {
          value: PaymentMethodEnum.BANK_DRAFT,
          label: intl.formatMessage({ id: 'suppliers.bankDraft' }),
        },
      ],
    },
    {
      id: 'max_payment_days',
      label: intl.formatMessage({ id: 'suppliers.maxPaymentDays' }),
      inputType: 'number',
    },
    {
      id: 'special_payment_due_date',
      label: intl.formatMessage({ id: 'suppliers.specialPaymentDueDate' }),
      inputType: 'number',
      min: 1,
      max: 31,
    },
    {
      id: 'is_non_payer',
      label: intl.formatMessage({ id: 'suppliers.isNonPayer' }),
      inputType: 'checkbox',
    },
    {
      id: 'discount_mode',
      label: intl.formatMessage({ id: 'suppliers.discountMode' }),
      inputType: 'select',
      options: [
        {
          value: 'absolute',
          label: intl.formatMessage({ id: 'suppliers.absolute' }),
        },
        {
          value: 'relative',
          label: intl.formatMessage({ id: 'suppliers.relative' }),
        },
      ],
    },
    {
      id: 'total_discounts',
      label: intl.formatMessage({ id: 'suppliers.totalDiscounts' }),
      inputType: 'number',
      step: 0.01,
    },
    {
      id: 'discount_on',
      label: intl.formatMessage({ id: 'suppliers.discountOn' }),
      inputType: 'select',
      options: [
        {
          value: 'order',
          label: intl.formatMessage({ id: 'suppliers.order' }),
        },
        {
          value: 'order_line',
          label: intl.formatMessage({ id: 'suppliers.orderLine' }),
        },
      ],
    },
    {
      id: 'state',
      label: intl.formatMessage({ id: 'suppliers.state' }),
      inputType: 'select',
      options: [
        {
          value: 'active',
          label: intl.formatMessage({ id: 'suppliers.active' }),
        },
        {
          value: 'inactive',
          label: intl.formatMessage({ id: 'suppliers.inactive' }),
        },
      ],
    },
    {
      id: 'created_at',
      label: intl.formatMessage({ id: 'suppliers.createdAt' }),
      inputType: 'date',
      readonly: true,
    },
    {
      id: 'contasol_id',
      label: intl.formatMessage({ id: 'distributorDetail.contasolIdLabel' }),
      inputType: 'text',
      readonly: !authorizedContasolEditors.includes(authUser?.email),
    },
  ];

  const fetchSupplier = async (id) => {
    try {
      const { data } = await retrieveSupplier(id);
      setSupplier(data);
    } catch (error) {
      console.warn(error);
    }
  };

  useEffect(() => {
    fetchSupplier(id);
  }, [id]);

  async function onSubmit(formState: SupplierModel) {
    try {
      setStatus(LOADING_STATUS);
      const changedFields = getChangedFields(supplier!, formState);
      const { data } = await updateSupplier({
        id: supplier!.id,
        ...changedFields,
      });
      setSupplier(data);
      setEditing(false);
      toast.success(intl.formatMessage({ id: 'supplierDetail.saved' }));
    } catch (error) {
      console.warn(error);
      if (error.response?.data) {
        setError(error.response?.data);
      }
      toast.error(intl.formatMessage({ id: 'supplierDetail.errorSaving' }));
    } finally {
      setStatus(IDLE_STATUS);
    }
  }

  return (
    <div className="card p-10">
      {status === 'loading_request_user' ? (
        <div className="spinner-border text-primary" role="status" />
      ) : (
        <>
          {supplier ? (
            <>
              <GenericForm
                columns={2}
                error={error}
                editing={editing}
                toggleEdit={() => setEditing(!editing)}
                title={intl.formatMessage({
                  id: 'supplierDetail.infoTitle',
                })}
                initialState={supplier}
                fields={formFields}
                ctaLabel={intl.formatMessage({
                  id: 'supplierDetail.saveChanges',
                })}
                onSubmit={onSubmit}
                submitting={status === LOADING_STATUS}
                submittingLabel={intl.formatMessage({
                  id: 'supplierDetail.pleaseWait',
                })}
              />
            </>
          ) : (
            <p>{intl.formatMessage({ id: 'supplierDetail.noSupplier' })}</p>
          )}
        </>
      )}
    </div>
  );
}
